import { Anchor } from '@studio/design-system/button';
import { Logo } from '@studio/design-system/logo';
import { Dots } from '@studio/design-system/pager';
import type { ReactElement } from 'react';
import { createContext, useState } from 'react';
import { twMerge } from 'cn';
import { Footer } from './footer.jsx';
import type { Props } from './types.js';

// ButtonBox renders after the content so actions cannot cover form fields.
export const WizardActionsContext = createContext<{ el: HTMLElement | null; inWizard: boolean }>({
  el: null,
  inWizard: false
});

/** `<Wizard>` alias. */
export { Wizard as Root };

/** `<Wizard>` component. */
export const Wizard = ({
  children,
  className,
  currentStep,
  scrollPage = true,
  secondaryAction,
  theme = 'primary',
  totalSteps,
  ...props
}: Props): ReactElement => {
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);

  // Let the full card grow so short windows use one page scroll area.
  return (
    <div className='max-sheet:scrollbar-hide h-[100dvh] overflow-y-auto px-16 py-16 [@media(max-width:479px)_and_(min-height:800px)]:py-24 [@media(min-width:480px)_and_(min-height:720px)]:py-40'>
      {/* Keep overflow top-aligned for keyboard access (ai.knowledge/global: keyboard-reachable-interactive-elements). */}
      <div
        className={twMerge(
          'flex flex-col items-center',
          scrollPage ? 'min-h-full' : 'h-full min-h-[560px] [@media(max-height:591px)]:h-auto'
        )}
      >
        <Anchor
          className='shrink-0'
          onClick={(): void => {
            window.location.href = '/';
          }}
        >
          <Logo
            className='pb-16 [@media(max-width:479px)_and_(min-height:800px)]:pb-32 [@media(max-width:479px)_and_(min-height:800px)]:pt-8 [@media(min-width:480px)_and_(min-height:720px)]:pb-40 [@media(min-width:480px)_and_(min-height:720px)]:pt-8'
            size='2x'
          />
        </Anchor>

        <WizardActionsContext.Provider value={{ el: actionsEl, inWizard: true }}>
          <div
            className={twMerge(
              'flex w-full max-w-[450px] flex-col overflow-hidden rounded-12 border-t-12 bg-light text-base-100 [@media(max-width:479px)_and_(min-height:800px)]:min-h-[550px]',
              scrollPage ? 'shrink-0' : 'min-h-0',
              theme === 'success' ? 'border-t-success' : 'border-t-primary'
            )}
          >
            {/* Tall phones retain the original card height and roomier spacing. */}
            <div
              className={twMerge(
                'm-24 flex flex-col items-center gap-20 break-words text-center [@media(min-width:480px)]:m-32 [@media(min-width:480px)_and_(min-height:720px)]:gap-28 [@media(max-width:479px)_and_(min-height:800px)]:m-32 [@media(max-width:479px)_and_(min-height:800px)]:flex-1 [@media(max-width:479px)_and_(min-height:800px)]:gap-28 [&>*]:max-w-full [&>*]:shrink-0',
                !scrollPage && 'max-sheet:scrollbar-hide min-h-0 overflow-x-hidden overflow-y-auto',
                className
              )}
              {...props}
            >
              {children}
            </div>
            <div
              className='flex shrink-0 flex-col items-center px-24 pb-24 empty:hidden [@media(max-width:479px)_and_(min-height:800px)]:px-32 [@media(max-width:479px)_and_(min-height:800px)]:pb-32 [@media(min-width:480px)]:px-32 [@media(min-width:480px)]:pb-32'
              ref={setActionsEl}
            />
          </div>
        </WizardActionsContext.Provider>

        {/* Secondary actions follow the card in both page and content scrolling layouts. */}
        {secondaryAction !== undefined && <div className='shrink-0 pt-12'>{secondaryAction}</div>}

        <Dots
          active={currentStep}
          className='shrink-0 py-12 [@media(max-width:479px)_and_(min-height:800px)]:py-16'
          total={totalSteps}
        />

        <Footer className='shrink-0 pb-0' />
      </div>
    </div>
  );
};
